<template>
  <div class="three-d-scene">
    <div ref="container" class="scene-container"></div>
    <div class="scene-controls">
      <div class="control-panel">
        <button @click="toggleFullscreen" class="control-btn">
          {{ isFullscreen ? "退出全屏" : "全屏" }}
        </button>
        <button @click="resetCamera" class="control-btn">重置视角</button>
        <button @click="toggleWireframe" class="control-btn">
          {{ showWireframe ? "实体模式" : "线框模式" }}
        </button>
      </div>
      <div class="scene-info">
        <span>FPS: {{ fps }}</span>
        <span>对象数量: {{ objectCount }}</span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from "vue";
import * as THREE from "three";

// Props
// eslint-disable-next-line no-undef
const props = defineProps({
  spaceData: {
    type: Object,
    default: () => ({}),
  },
  editMode: {
    type: Boolean,
    default: false,
  },
});

// Emits
// eslint-disable-next-line no-undef
const emit = defineEmits(["space-updated"]);

// 响应式数据
const container = ref(null);
const isFullscreen = ref(false);
const showWireframe = ref(false);
const fps = ref(0);
const objectCount = ref(0);

// Three.js 相关变量
let scene, camera, renderer;
let animationId;
let frameCount = 0;
let lastTime = 0;

// 3D对象管理
let objects = [];
let selectedObject = null;
let isDragging = false;
let mouse = new THREE.Vector2();
let raycaster = new THREE.Raycaster();

// 初始化3D场景
const initScene = () => {
  if (!container.value) return;

  // 创建场景
  scene = new THREE.Scene();
  scene.background = new THREE.Color(0x87ceeb); // 天蓝色背景

  // 创建相机
  camera = new THREE.PerspectiveCamera(
    75,
    container.value.clientWidth / container.value.clientHeight,
    0.1,
    1000
  );
  camera.position.set(0, 5, 10);

  // 创建渲染器
  renderer = new THREE.WebGLRenderer({ antialias: true });
  renderer.setSize(container.value.clientWidth, container.value.clientHeight);
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;
  container.value.appendChild(renderer.domElement);

  // 添加控制器
  setupControls();

  // 添加灯光
  setupLights();

  // 根据模式创建场景
  if (props.editMode) {
    createEditModeScene();
    setupEventListeners();
  } else {
    createBasicScene();
  }

  // 开始渲染循环
  animate();
};

// 设置控制器
const setupControls = () => {
  let mouseX = 0,
    mouseY = 0;
  let isMouseDown = false;

  const onMouseMove = (event) => {
    if (!isMouseDown) return;

    mouseX = (event.clientX / window.innerWidth) * 2 - 1;
    mouseY = -(event.clientY / window.innerHeight) * 2 + 1;

    camera.position.x = mouseX * 10;
    camera.position.y = mouseY * 10 + 5;
    camera.lookAt(0, 0, 0);
  };

  const onMouseDown = () => {
    isMouseDown = true;
  };

  const onMouseUp = () => {
    isMouseDown = false;
  };

  renderer.domElement.addEventListener("mousemove", onMouseMove);
  renderer.domElement.addEventListener("mousedown", onMouseDown);
  renderer.domElement.addEventListener("mouseup", onMouseUp);
};

// 设置灯光
const setupLights = () => {
  // 环境光
  const ambientLight = new THREE.AmbientLight(0x404040, 0.6);
  scene.add(ambientLight);

  // 方向光
  const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
  directionalLight.position.set(10, 10, 5);
  directionalLight.castShadow = true;
  directionalLight.shadow.mapSize.width = 2048;
  directionalLight.shadow.mapSize.height = 2048;
  scene.add(directionalLight);

  // 点光源
  const pointLight = new THREE.PointLight(0xffffff, 0.5, 100);
  pointLight.position.set(-10, 10, -10);
  scene.add(pointLight);
};

// 创建基础场景
const createBasicScene = () => {
  // 创建地面
  const groundGeometry = new THREE.PlaneGeometry(50, 50);
  const groundMaterial = new THREE.MeshLambertMaterial({ color: 0x90ee90 });
  const ground = new THREE.Mesh(groundGeometry, groundMaterial);
  ground.rotation.x = -Math.PI / 2;
  ground.receiveShadow = true;
  scene.add(ground);

  // 创建一些基础几何体
  const geometries = [
    new THREE.BoxGeometry(2, 2, 2),
    new THREE.SphereGeometry(1, 32, 32),
    new THREE.ConeGeometry(1, 3, 8),
    new THREE.CylinderGeometry(1, 1, 3, 8),
  ];

  const materials = [
    new THREE.MeshLambertMaterial({ color: 0xff6b6b }),
    new THREE.MeshLambertMaterial({ color: 0x4ecdc4 }),
    new THREE.MeshLambertMaterial({ color: 0x45b7d1 }),
    new THREE.MeshLambertMaterial({ color: 0xf9ca24 }),
  ];

  geometries.forEach((geometry, index) => {
    const material = materials[index];
    const mesh = new THREE.Mesh(geometry, material);
    mesh.position.set(
      (Math.random() - 0.5) * 20,
      geometry.parameters.height ? geometry.parameters.height / 2 : 1,
      (Math.random() - 0.5) * 20
    );
    mesh.castShadow = true;
    scene.add(mesh);
  });

  // 更新对象数量
  objectCount.value = scene.children.length;
};

// 创建编辑模式场景
const createEditModeScene = () => {
  // 创建地面
  const groundGeometry = new THREE.PlaneGeometry(50, 50);
  const groundMaterial = new THREE.MeshLambertMaterial({ color: 0x90ee90 });
  const ground = new THREE.Mesh(groundGeometry, groundMaterial);
  ground.rotation.x = -Math.PI / 2;
  ground.receiveShadow = true;
  ground.userData = { type: "ground" };
  scene.add(ground);

  // 加载空间数据中的对象
  if (props.spaceData && props.spaceData.objects) {
    props.spaceData.objects.forEach((objData) => {
      addObjectFromData(objData);
    });
  }

  // 更新对象数量
  objectCount.value = scene.children.length;
};

// 从数据添加对象
const addObjectFromData = (objData) => {
  const { type, position, rotation, scale, material } = objData;
  const mesh = createObject(type, material);

  if (position) mesh.position.set(position.x, position.y, position.z);
  if (rotation) mesh.rotation.set(rotation.x, rotation.y, rotation.z);
  if (scale) mesh.scale.set(scale.x, scale.y, scale.z);

  mesh.userData = { type, id: Date.now() };
  objects.push(mesh);
  scene.add(mesh);
};

// 创建3D对象
const createObject = (type, materialName = "红色") => {
  let geometry;

  // 材质颜色映射
  const materialColors = {
    红色: 0xff6b6b,
    蓝色: 0x4ecdc4,
    绿色: 0x45b7d1,
    黄色: 0xf9ca24,
    紫色: 0x6c5ce7,
    橙色: 0xfd79a8,
    灰色: 0x636e72,
    黑色: 0x2d3436,
  };

  const color = materialColors[materialName] || 0xff6b6b;

  switch (type) {
    case "cube":
      geometry = new THREE.BoxGeometry(2, 2, 2);
      break;
    case "sphere":
      geometry = new THREE.SphereGeometry(1, 32, 32);
      break;
    case "cylinder":
      geometry = new THREE.CylinderGeometry(1, 1, 3, 8);
      break;
    case "plane":
      geometry = new THREE.PlaneGeometry(4, 4);
      break;
    default:
      geometry = new THREE.BoxGeometry(2, 2, 2);
  }

  const material = new THREE.MeshLambertMaterial({ color });
  const mesh = new THREE.Mesh(geometry, material);

  // 设置随机位置
  mesh.position.set(
    (Math.random() - 0.5) * 10,
    type === "plane" ? 0 : 1,
    (Math.random() - 0.5) * 10
  );

  mesh.castShadow = true;
  mesh.userData = { type, id: Date.now() };

  return mesh;
};

// 设置事件监听器
const setupEventListeners = () => {
  // 监听自定义事件
  window.addEventListener("add-3d-object", handleAddObject);
  window.addEventListener("clear-3d-scene", handleClearScene);
  window.addEventListener("reset-3d-camera", handleResetCamera);

  // 鼠标事件
  renderer.domElement.addEventListener("mousedown", onMouseDown);
  renderer.domElement.addEventListener("mousemove", onMouseMove);
  renderer.domElement.addEventListener("mouseup", onMouseUp);
  renderer.domElement.addEventListener("click", onMouseClick);
};

// 事件处理函数
const handleAddObject = (event) => {
  const { type, material } = event.detail;
  const mesh = createObject(type, material);
  objects.push(mesh);
  scene.add(mesh);
  objectCount.value = scene.children.length;
  saveSpaceData();
};

const handleClearScene = () => {
  objects.forEach((obj) => {
    scene.remove(obj);
  });
  objects = [];
  objectCount.value = scene.children.length;
  saveSpaceData();
};

const handleResetCamera = () => {
  camera.position.set(0, 5, 10);
  camera.lookAt(0, 0, 0);
};

// 鼠标事件处理
const onMouseDown = (event) => {
  isDragging = true;
  mouse.x = (event.clientX / window.innerWidth) * 2 - 1;
  mouse.y = -(event.clientY / window.innerHeight) * 2 + 1;
};

const onMouseMove = (event) => {
  if (!isDragging) return;

  mouse.x = (event.clientX / window.innerWidth) * 2 - 1;
  mouse.y = -(event.clientY / window.innerHeight) * 2 + 1;

  if (selectedObject) {
    // 简单的拖拽逻辑
    const vector = new THREE.Vector3(mouse.x, mouse.y, 0.5);
    vector.unproject(camera);
    const dir = vector.sub(camera.position).normalize();
    const distance = -camera.position.z / dir.z;
    const pos = camera.position.clone().add(dir.multiplyScalar(distance));
    selectedObject.position.x = pos.x;
    selectedObject.position.z = pos.z;
  }
};

const onMouseUp = () => {
  isDragging = false;
  if (selectedObject) {
    saveSpaceData();
  }
};

const onMouseClick = (event) => {
  mouse.x = (event.clientX / window.innerWidth) * 2 - 1;
  mouse.y = -(event.clientY / window.innerHeight) * 2 + 1;

  raycaster.setFromCamera(mouse, camera);
  const intersects = raycaster.intersectObjects(objects);

  if (intersects.length > 0) {
    // 取消之前选中的对象
    if (selectedObject) {
      selectedObject.material.emissive.setHex(0x000000);
    }

    // 选中新对象
    selectedObject = intersects[0].object;
    selectedObject.material.emissive.setHex(0x444444);
  } else {
    // 取消选中
    if (selectedObject) {
      selectedObject.material.emissive.setHex(0x000000);
      selectedObject = null;
    }
  }
};

// 保存空间数据
const saveSpaceData = () => {
  const spaceObjects = objects.map((obj) => ({
    type: obj.userData.type,
    position: { x: obj.position.x, y: obj.position.y, z: obj.position.z },
    rotation: { x: obj.rotation.x, y: obj.rotation.y, z: obj.rotation.z },
    scale: { x: obj.scale.x, y: obj.scale.y, z: obj.scale.z },
    material: getMaterialName(obj.material.color.getHex()),
  }));

  emit("space-updated", { objects: spaceObjects });
};

// 获取材质名称
const getMaterialName = (hex) => {
  const colorMap = {
    0xff6b6b: "红色",
    0x4ecdc4: "蓝色",
    0x45b7d1: "绿色",
    0xf9ca24: "黄色",
    0x6c5ce7: "紫色",
    0xfd79a8: "橙色",
    0x636e72: "灰色",
    0x2d3436: "黑色",
  };
  return colorMap[hex] || "红色";
};

// 动画循环
const animate = () => {
  animationId = requestAnimationFrame(animate);

  // 旋转场景中的对象
  scene.children.forEach((child) => {
    if (child.isMesh && child.geometry.type !== "PlaneGeometry") {
      child.rotation.y += 0.01;
      child.rotation.x += 0.005;
    }
  });

  // 计算FPS
  frameCount++;
  const currentTime = performance.now();
  if (currentTime - lastTime >= 1000) {
    fps.value = Math.round((frameCount * 1000) / (currentTime - lastTime));
    frameCount = 0;
    lastTime = currentTime;
  }

  renderer.render(scene, camera);
};

// 控制方法
const toggleFullscreen = () => {
  if (!document.fullscreenElement) {
    container.value.requestFullscreen();
    isFullscreen.value = true;
  } else {
    document.exitFullscreen();
    isFullscreen.value = false;
  }
};

const resetCamera = () => {
  camera.position.set(0, 5, 10);
  camera.lookAt(0, 0, 0);
};

const toggleWireframe = () => {
  showWireframe.value = !showWireframe.value;
  scene.children.forEach((child) => {
    if (child.isMesh) {
      child.material.wireframe = showWireframe.value;
    }
  });
};

// 窗口大小调整
const onWindowResize = () => {
  if (!container.value) return;

  camera.aspect = container.value.clientWidth / container.value.clientHeight;
  camera.updateProjectionMatrix();
  renderer.setSize(container.value.clientWidth, container.value.clientHeight);
};

// 清理资源
const dispose = () => {
  if (animationId) {
    cancelAnimationFrame(animationId);
  }

  if (renderer) {
    renderer.dispose();
  }

  if (scene) {
    scene.clear();
  }

  // 清理事件监听器
  window.removeEventListener("add-3d-object", handleAddObject);
  window.removeEventListener("clear-3d-scene", handleClearScene);
  window.removeEventListener("reset-3d-camera", handleResetCamera);
};

// 生命周期
onMounted(() => {
  initScene();
  window.addEventListener("resize", onWindowResize);
});

onUnmounted(() => {
  dispose();
  window.removeEventListener("resize", onWindowResize);
});
</script>

<style lang="scss" scoped>
.three-d-scene {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.scene-container {
  width: 100%;
  height: 100%;
  cursor: grab;

  &:active {
    cursor: grabbing;
  }
}

.scene-controls {
  position: absolute;
  top: 20px;
  left: 20px;
  z-index: 100;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.control-panel {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.control-btn {
  padding: 8px 16px;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: background 0.3s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.9);
  }
}

.scene-info {
  display: flex;
  flex-direction: column;
  gap: 5px;
  color: white;
  font-size: 12px;
  background: rgba(0, 0, 0, 0.7);
  padding: 10px;
  border-radius: 4px;
}
</style>
